import React, { createContext, useContext, useState, useEffect } from 'react';
import { loadAppConfig, saveAppConfig } from '../services/configService';

const LanguageContext = createContext();

export const useLanguage = () => {
  const context = useContext(LanguageContext);
  if (!context) {
    throw new Error('useLanguage must be used within a LanguageProvider');
  }
  return context;
};

const translations = {
  en: {
    // App Bar
    appTitle: 'Document Search',
    
    // Hero Section
    heroTitle: 'Product Technical Documents',
    heroSubtitle: 'Search and download technical documents by entering the product serial number',
    
    // Search Bar
    searchLabel: 'Search by Serial Number',
    searchPlaceholder: 'Enter Serial Number',
    searchAriaLabel: 'search by serial number',
    searchButton: 'search',
    clearButton: 'clear search',
    cameraButton: 'scan with camera',
    uploadButton: 'upload image',
    scanningImage: 'Reading text from image...',
    scanSuccess: 'Found',
    scanNoText: 'No text found in image',
    scanError: 'Error reading image',
    scanPartialSerial: 'Only serial found',
    scanPartialArticle: 'Only article found',
    searchModeLabel: 'Choose search mode',
    cameraAIButton: 'Scan with AI',
    manualSearchButton: 'Manual Search',
    searchLabelCamera: 'Scan Serial Number',
    searchLabelManual: 'Manual Search',
    serialNumberField: 'Serial Number',
    serialNumberPlaceholder: 'Enter serial number',
    articleCodeField: 'Article Code',
    articleCodePlaceholder: 'Enter article code',
    
    // Document List
    documentsFound: 'Documents Found',
    noDocuments: 'No documents found',
    noDocumentsMessage: 'Try searching with a different serial number',
    downloadButton: 'Download',
    downloadDocumentAriaLabel: 'download document',
    documentSizeLabel: 'Size',
    documentModifiedLabel: 'Modified',
    unknownSize: 'Unknown size',
    unknownDate: 'Unknown date',
    
    // Loading
    loadingMessage: 'Searching for documents...',
    loadingMoreDocuments: 'Loading more documents...',

    // Product Information
    productInfoTitle: 'Product Information',
    productInfoItemCode: 'Item Code',
    productInfoSerialNumber: 'Serial Number',
    productInfoOrder: 'Order',
    productInfoSerial: 'Serial Number',
    productInfoYourReference: 'Your Reference No.',
    
    // Footer
    footerText: 'powered by',
    companyName: 'Phyo',
    
    // Admin Login
    adminLoginTitle: 'Admin Access',
    adminLoginSubtitle: 'Enter your credentials to access the setup page',
    adminLoginUsername: 'Username',
    adminLoginPassword: 'Password',
    adminLoginButton: 'Login',
    adminLoginEmptyFields: 'Please enter both username and password',
    adminLoginInvalidCredentials: 'Invalid username or password',
    
    // Setup Page
    setupPageTitle: 'Admin Setup',
    currentVersion: 'Current Version',
    features: 'Features',
    featureSearch: 'Document search by serial number',
    featureDownload: 'Direct download from SharePoint',
    featureOAuth: 'OAuth authentication with Microsoft',
    featureResponsive: 'Responsive mobile-first design',
    featureMultilang: 'Multi-language support (IT/EN)',
    featureMaterial: 'Modern Material Design UI',
    themeColors: 'Theme Colors',
    reset: 'Reset',
    topBarSection: 'Top Bar',
    topBarBackground: 'Top Bar Background',
    topBarBackgroundDescription: 'Background color of the navigation bar at the top',
    topBarText: 'Top Bar Text',
    topBarTextDescription: 'Text color in the navigation bar',
    bodySection: 'Body',
    bodyBackground: 'Body Background',
    bodyBackgroundDescription: 'Main background color of the page',
    bodyPaper: 'Paper Color',
    bodyPaperDescription: 'Background color for content cards and panels',
    bodyPrimaryButton: 'Primary Button',
    bodyPrimaryButtonDescription: 'Color of primary action buttons',
    bodyPrimaryButtonHover: 'Button Hover',
    bodyPrimaryButtonHoverDescription: 'Color when hovering over primary buttons',
    bodyPrimaryButtonText: 'Primary Button Text',
    bodyPrimaryButtonTextDescription: 'Text color on primary buttons',
    textColorsSection: 'Text Colors',
    textPrimary: 'Primary Text',
    textPrimaryDescription: 'Main text color throughout the application',
    textSecondary: 'Secondary Text',
    textSecondaryDescription: 'Color for secondary or helper text',
    footerSection: 'Footer',
    footerBackground: 'Footer Background',
    footerBackgroundDescription: 'Background color of the page footer',
    footerTextColor: 'Footer Text Color',
    footerTextColorDescription: 'Text color in the footer area',
    logoUpload: 'Logo Upload',
    logoUploadDescription: 'Upload a logo for the top bar (max 2MB, PNG/JPG)',
    uploadLogo: 'Upload Logo',
    logoTooLarge: 'Logo file is too large (max 2MB)',
    environmentVariables: 'Environment Variables',
    envVarWarning: 'Note: These values are read from environment variables at build time. To apply changes, update the .env file and rebuild the application.',
    clientIdHelp: 'Azure AD Application (client) ID',
    tenantIdHelp: 'Azure AD Directory (tenant) ID',
    clientSecretHelp: 'Azure AD Client Secret',
    siteUrlHelp: 'SharePoint site URL',
    libraryNameHelp: 'SharePoint document library name',
    siteIdHelp: 'SharePoint site ID',
    listIdHelp: 'SharePoint list ID (GUID)',
    adminUsernameHelp: 'Admin username for setup page',
    adminPasswordHelp: 'Admin password for setup page',
    saveConfiguration: 'Save Configuration',
    configSaved: 'Configuration saved. Note: Environment variables need to be updated in .env file and app needs to be rebuilt for changes to take effect.',
    configSavedRuntime: 'Configuration saved! Click "Apply and Reload" to apply changes.',
    applyAndReload: 'Apply and Reload',
    exportEnvFile: 'Export .env file',
    envFileExported: '.env file exported successfully!',
    resetConfig: 'Reset Configuration',
    configReset: 'Configuration reset to .env values. Reload the page to apply.',
    envVarWarningRuntime: 'Configuration changes are saved in the browser and applied immediately after reloading. No need to modify the .env file.',
    envSecurityWarning: '⚠️ SECURITY WARNING: Credentials are stored in the browser. Do not share this device with unauthorized people.',
    colorsReset: 'Colors reset to default',
    textsSection: 'Text Customization',
    textsDescription: 'Customize the text displayed in the application for both languages',
    textsSaveButton: 'Save Texts',
    textsResetButton: 'Reset Texts',
    textsReset: 'Texts reset to default',
    languageTabEN: 'English',
    languageTabIT: 'Italian',
    dataverseConfiguration: 'Dataverse Configuration',
    dataverseBaseUrl: 'Dataverse Base URL',
    dataverseBaseUrlHelp: 'Base URL of Dataverse (e.g.: https://orgc0465cb6.crm4.dynamics.com)',
    dataverseEntitySet: 'Dataverse Entity Set',
    dataverseEntitySetHelp: 'Name of the Dataverse entity set for serial number search',
    dataverseScope: 'Dataverse Scope',
    dataverseScopeHelp: 'OAuth scope for Dataverse (e.g.: https://orgc0465cb6.crm4.dynamics.com/.default)',
    corsProxyUrl: 'CORS Proxy URL',
    corsProxyUrlHelp: 'CORS proxy URL for API calls (e.g., https://corsproxy.io/? or https://api.allorigins.win/raw?url=). Leave empty for direct calls.',
    backendApiUrl: 'Backend API URL',
    backendApiUrlHelp: 'Backend API URL (e.g., https://fireco-app.azurewebsites.net). Leave empty to use CORS proxy.',
  },
  it: {
    // App Bar
    appTitle: 'Ricerca Documenti',
    
    // Hero Section
    heroTitle: 'Documenti Tecnici Prodotto',
    heroSubtitle: 'Cerca e scarica documenti tecnici inserendo il numero seriale del prodotto',
    
    // Search Bar
    searchLabel: 'Ricerca per Numero Seriale',
    searchPlaceholder: 'Inserisci Numero Seriale',
    searchAriaLabel: 'ricerca per numero seriale',
    searchButton: 'cerca',
    clearButton: 'cancella ricerca',
    cameraButton: 'scansiona con fotocamera',
    uploadButton: 'carica immagine',
    scanningImage: 'Lettura testo dall\'immagine...',
    scanSuccess: 'Trovato',
    scanNoText: 'Nessun testo trovato nell\'immagine',
    scanError: 'Errore nella lettura dell\'immagine',
    scanPartialSerial: 'Trovato solo seriale',
    scanPartialArticle: 'Trovato solo articolo',
    searchModeLabel: 'Scegli modalità di ricerca',
    cameraAIButton: 'Scansiona con AI',
    manualSearchButton: 'Ricerca Manuale',
    searchLabelCamera: 'Scansiona Numero Seriale',
    searchLabelManual: 'Ricerca Manuale',
    serialNumberField: 'Numero Seriale',
    serialNumberPlaceholder: 'Inserisci numero seriale',
    articleCodeField: 'Codice Articolo',
    articleCodePlaceholder: 'Inserisci codice articolo',
    
    // Document List
    documentsFound: 'Documenti Trovati',
    noDocuments: 'Nessun documento trovato',
    noDocumentsMessage: 'Prova a cercare con un numero seriale diverso',
    downloadButton: 'Scarica',
    downloadDocumentAriaLabel: 'scarica documento',
    documentSizeLabel: 'Dimensione',
    documentModifiedLabel: 'Modificato',
    unknownSize: 'Dimensione sconosciuta',
    unknownDate: 'Data sconosciuta',
    
    // Loading
    loadingMessage: 'Ricerca documenti in corso...',
    loadingMoreDocuments: 'Caricamento altri documenti...',

    // Product Information
    productInfoTitle: 'Informazioni Prodotto',
    productInfoItemCode: 'Codice Articolo',
    productInfoSerialNumber: 'Numero Seriale',
    productInfoOrder: 'Commessa',
    productInfoSerial: 'Matricola',
    productInfoYourReference: 'Vs. Riferimento',
    
    // Footer
    footerText: 'powered by',
    companyName: 'Phyo',
    
    // Admin Login
    adminLoginTitle: 'Accesso Admin',
    adminLoginSubtitle: 'Inserisci le tue credenziali per accedere alla pagina di setup',
    adminLoginUsername: 'Nome Utente',
    adminLoginPassword: 'Password',
    adminLoginButton: 'Accedi',
    adminLoginEmptyFields: 'Inserisci sia nome utente che password',
    adminLoginInvalidCredentials: 'Nome utente o password non validi',
    
    // Setup Page
    setupPageTitle: 'Setup Admin',
    currentVersion: 'Versione Attuale',
    features: 'Funzionalità',
    featureSearch: 'Ricerca documenti per numero seriale',
    featureDownload: 'Download diretto da SharePoint',
    featureOAuth: 'Autenticazione OAuth con Microsoft',
    featureResponsive: 'Design responsive mobile-first',
    featureMultilang: 'Supporto multilingua (IT/EN)',
    featureMaterial: 'Interfaccia moderna Material Design',
    themeColors: 'Colori Tema',
    reset: 'Ripristina',
    topBarSection: 'Barra Superiore',
    topBarBackground: 'Sfondo Barra',
    topBarBackgroundDescription: 'Colore di sfondo della barra di navigazione superiore',
    topBarText: 'Testo Barra',
    topBarTextDescription: 'Colore del testo nella barra di navigazione',
    bodySection: 'Corpo',
    bodyBackground: 'Sfondo Corpo',
    bodyBackgroundDescription: 'Colore di sfondo principale della pagina',
    bodyPaper: 'Colore Carta',
    bodyPaperDescription: 'Colore di sfondo per le schede e i pannelli di contenuto',
    bodyPrimaryButton: 'Pulsante Primario',
    bodyPrimaryButtonDescription: 'Colore dei pulsanti di azione primari',
    bodyPrimaryButtonHover: 'Pulsante Hover',
    bodyPrimaryButtonHoverDescription: 'Colore al passaggio del mouse sui pulsanti primari',
    bodyPrimaryButtonText: 'Testo Pulsante Primario',
    bodyPrimaryButtonTextDescription: 'Colore del testo sui pulsanti primari',
    textColorsSection: 'Colori Testo',
    textPrimary: 'Testo Primario',
    textPrimaryDescription: "Colore principale del testo in tutta l'applicazione",
    textSecondary: 'Testo Secondario',
    textSecondaryDescription: 'Colore per il testo secondario o di aiuto',
    footerSection: 'Footer Inferiore',
    footerBackground: 'Sfondo Footer',
    footerBackgroundDescription: 'Colore di sfondo del piè di pagina',
    footerTextColor: 'Colore Testo Footer',
    footerTextColorDescription: "Colore del testo nell'area del footer",
    logoUpload: 'Carica Logo',
    logoUploadDescription: 'Carica un logo per la barra superiore (max 2MB, PNG/JPG)',
    uploadLogo: 'Carica Logo',
    logoTooLarge: 'Il file logo è troppo grande (max 2MB)',
    environmentVariables: 'Variabili d\'Ambiente',
    envVarWarning: "Nota: Questi valori sono letti dalle variabili d'ambiente al momento della build. Per applicare le modifiche, aggiorna il file .env e ricompila l'applicazione.",
    clientIdHelp: 'ID Applicazione (client) Azure AD',
    tenantIdHelp: 'ID Directory (tenant) Azure AD',
    clientSecretHelp: 'Client Secret Azure AD',
    siteUrlHelp: 'URL del sito SharePoint',
    libraryNameHelp: 'Nome della libreria documenti SharePoint',
    siteIdHelp: 'ID del sito SharePoint',
    listIdHelp: 'ID della lista SharePoint (GUID)',
    adminUsernameHelp: 'Nome utente admin per la pagina di setup',
    adminPasswordHelp: 'Password admin per la pagina di setup',
    saveConfiguration: 'Salva Configurazione',
    configSaved: "Configurazione salvata. Nota: Le variabili d'ambiente devono essere aggiornate nel file .env e l'app deve essere ricompilata per rendere effettive le modifiche.",
    configSavedRuntime: 'Configurazione salvata! Clicca "Applica e Ricarica" per applicare le modifiche.',
    applyAndReload: 'Applica e Ricarica',
    exportEnvFile: 'Esporta file .env',
    envFileExported: 'File .env esportato con successo!',
    resetConfig: 'Reset Configurazione',
    configReset: 'Configurazione ripristinata ai valori del file .env. Ricarica la pagina per applicare.',
    envVarWarningRuntime: 'Le modifiche alla configurazione vengono salvate nel browser e applicate immediatamente dopo il ricaricamento. Non è necessario modificare il file .env.',
    envSecurityWarning: '⚠️ ATTENZIONE SICUREZZA: Le credenziali vengono salvate nel browser. Non condividere questo dispositivo con persone non autorizzate.',
    colorsReset: 'Colori ripristinati ai valori predefiniti',
    textsSection: 'Personalizzazione Testi',
    textsDescription: "Personalizza i testi visualizzati nell'applicazione per entrambe le lingue",
    textsSaveButton: 'Salva Testi',
    textsResetButton: 'Ripristina Testi',
    textsReset: 'Testi ripristinati ai valori predefiniti',
    languageTabEN: 'Inglese',
    languageTabIT: 'Italiano',
    dataverseConfiguration: 'Configurazione Dataverse',
    dataverseBaseUrl: 'URL Base Dataverse',
    dataverseBaseUrlHelp: 'URL base di Dataverse (es: https://orgc0465cb6.crm4.dynamics.com)',
    dataverseEntitySet: 'Entity Set Dataverse',
    dataverseEntitySetHelp: 'Nome dell\'entity set Dataverse per la ricerca matricole',
    dataverseScope: 'Scope Dataverse',
    dataverseScopeHelp: 'Scope OAuth per Dataverse (es: https://orgc0465cb6.crm4.dynamics.com/.default)',
    corsProxyUrl: 'URL Proxy CORS',
    corsProxyUrlHelp: 'URL del proxy CORS per le chiamate API (es: https://corsproxy.io/? oppure https://api.allorigins.win/raw?url=). Lascia vuoto per chiamate dirette.',
    backendApiUrl: 'URL Backend API',
    backendApiUrlHelp: 'URL del backend API (es: https://fireco-app.azurewebsites.net). Lascia vuoto per usare il CORS proxy.',
  },
};

export const LanguageProvider = ({ children }) => {
  const [language, setLanguage] = useState('en'); // Default to English
  const [customTranslations, setCustomTranslations] = useState({});
  const [isLoaded, setIsLoaded] = useState(false);

  // Load custom translations from API on mount
  useEffect(() => {
    const loadTranslations = async () => {
      try {
        const config = await loadAppConfig();
        if (config && config.translations) {
          setCustomTranslations(config.translations);
        }
      } catch (error) {
        console.error('Failed to load translations from API:', error);
        // Continue with empty custom translations
      }
      setIsLoaded(true);
    };
    loadTranslations();
  }, []);

  const toggleLanguage = () => {
    setLanguage((prev) => (prev === 'en' ? 'it' : 'en'));
  };

  const t = (key) => {
    // Check for custom translation first
    if (customTranslations[language] && customTranslations[language][key]) {
      return customTranslations[language][key];
    }
    // Fall back to default translations
    return translations[language][key] || key;
  };

  const updateCustomTranslations = async (newTranslations) => {
    setCustomTranslations(newTranslations);
    
    // Save to API
    try {
      const config = await loadAppConfig();
      const updatedConfig = {
        ...config,
        translations: newTranslations,
      };
      await saveAppConfig(updatedConfig);
    } catch (error) {
      console.error('Failed to save translations to API:', error);
    }
  };

  const resetCustomTranslations = async () => {
    setCustomTranslations({});
    
    // Save to API
    try {
      const config = await loadAppConfig();
      const updatedConfig = {
        ...config,
        translations: { en: {}, it: {} },
      };
      await saveAppConfig(updatedConfig);
    } catch (error) {
      console.error('Failed to reset translations in API:', error);
    }
  };

  // Update translations locally without saving to API
  const setCustomTranslationsLocal = (newTranslations) => {
    setCustomTranslations(newTranslations);
  };

  const getDefaultTranslations = () => {
    return translations;
  };

  return (
    <LanguageContext.Provider value={{ 
      language, 
      toggleLanguage, 
      t,
      customTranslations,
      updateCustomTranslations,
      resetCustomTranslations,
      setCustomTranslationsLocal,
      getDefaultTranslations,
      isLoaded
    }}>
      {children}
    </LanguageContext.Provider>
  );
};
